import { ISkiaRect } from '@core/entities/skia/SkiaRect/ISkiaRect'
import { plainToInstance } from 'class-transformer'
import { Point } from '@core/entities/skia/types'
import { SkiaShapeBase } from '@core/entities/skia/SkiaShapeBase/SkiaShapeBase'
import { SiteBoxDocumentAnnotation } from '@core/entities/SiteBoxDocumentAnnotation'
import { GroupMetadata } from '@core/entities/skia/types'
import { Matrix4 } from '@shopify/react-native-skia'
export class SkiaRect extends SkiaShapeBase<ISkiaRect> {
  id: string
  type: 'RECT'
  x: number
  y: number
  width: number
  height: number
  matrix: Matrix4

  public static new(payload: unknown): SkiaRect {
    return plainToInstance(SkiaRect, payload)
  }

  public static newFromSiteboxDocumentAnnotation(
    payload: SiteBoxDocumentAnnotation,
    metadata?: GroupMetadata,
  ): SkiaRect {
    const [x, y, width, height] = payload.annotationData.bbox

    let transformToSkiaRect = {
      id: payload.uuid,
      type: 'RECT',
      x: x,
      y: y,
      width: width,
      height: height,
    }

    if (metadata) {
      transformToSkiaRect = {
        ...transformToSkiaRect,
        ...metadata,
      }
    }

    return plainToInstance(SkiaRect, transformToSkiaRect)
  }

  public static newFromSiteboxDocumentAnnotationImage(
    payload: SiteBoxDocumentAnnotation,
    {
      widthPixelsPerPoint,
      heightPixelsPerPoint,
    }: {
      widthPixelsPerPoint: number
      heightPixelsPerPoint: number
    },
    metadata?: GroupMetadata,
  ): SkiaRect {
    const [x, y, width, height] = payload.annotationData.bbox

    let transformToSkiaRect = {
      id: payload.uuid,
      type: 'RECT',
      x: x * widthPixelsPerPoint,
      y: y * heightPixelsPerPoint,
      width: width * widthPixelsPerPoint,
      height: height * heightPixelsPerPoint,
    }

    if (metadata) {
      transformToSkiaRect = {
        ...transformToSkiaRect,
        ...metadata,
      }
    }
    return plainToInstance(SkiaRect, transformToSkiaRect)
  }

  isInBounds({ x, y }: Point): boolean {
    return (
      x >= this.x &&
      x <= this.x + this.width &&
      y >= this.y &&
      y <= this.y + this.height
    )
  }

  dragCorners(): Point[] {
    return [
      { x: this.x, y: this.y }, // top left
      { x: this.x + this.width, y: this.y }, // top right
      { x: this.x, y: this.y + this.height }, // bottom left
      { x: this.x + this.width, y: this.y + this.height }, // bottom right
    ]
  }

  resizeOnDrag({
    cornerIndex,
    x,
    y,
  }: {
    cornerIndex: number
    x: number
    y: number
  }) {
    switch (cornerIndex) {
      case 0: // top-left
        this.width += this.x - x
        this.height += this.y - y
        this.x = x
        this.y = y
        break
      case 1: // top-right
        this.width = x - this.x
        this.height += this.y - y
        this.y = y
        break
      case 2: // bottom-left
        this.width += this.x - x
        this.height = y - this.y
        this.x = x
        break
      case 3: // bottom-right
        this.width = x - this.x
        this.height = y - this.y
        break
      default:
        break
    }

    // Stop the square from flipping inside out
    if (this.width < 0) {
      this.width = 0
    }
    if (this.height < 0) {
      this.height = 0
    }
  }

  move({ x, y }: Point) {
    this.x = x
    this.y = y
  }

  moveByDelta(currentPoint: Point, previousPoint: Point) {
    this.move({
      x: this.x + (currentPoint.x - previousPoint.x),
      y: this.y + (currentPoint.y - previousPoint.y),
    })
  }

  labelAnchor(): Point {
    return { x: this.x, y: this.y }
  }

  updateFromPoints(startPoint: Point, endPoint: Point): void {
    this.x = Math.min(startPoint.x, endPoint.x)
    this.y = Math.min(startPoint.y, endPoint.y)
    this.width = Math.abs(endPoint.x - startPoint.x)
    this.height = Math.abs(endPoint.y - startPoint.y)
  }

  shapeCardAnchor(): Point {
    return { x: this.x, y: this.y + this.height }
  }

  hasSize(): boolean {
    return this.width > 0 && this.height > 0
  }
}
